import { useCallback, useMemo, useState } from 'react';

import { observer } from 'mobx-react';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { Badge, DropdownMenu } from '@bringg/react-components';
import { useTranslation } from 'react-i18next';
import { Portal } from '@bringg-frontend/bringg-web-infra';
import { useHasFeatureFlag } from '@bringg-frontend/hooks';

import { executeAction } from 'bringg-web/services/cross-application/cross-application';
import { ModalAction } from 'bringg-web/services/cross-application/cross-application.actions';
import { useStores } from 'bringg-web/recipes';
import { useWhatsNewIndicator } from './hooks/use-whats-new-indicator';
import newBadgeSvg from './new-badge.svg';

import './help-menu.scss';

type MenuItem = { key: string; label: JSX.Element; onClick?: () => unknown };

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const BADGE_DOT_OFFSET: [number, number] = [6, 0];

export const HelpMenu = observer(() => {
	const { t } = useTranslation();
	const {
		authStore,
		merchantConfigurationsStore: { configuration }
	} = useStores();
	const isWhatsNewIndicatorEnabled = useHasFeatureFlag('whats_new_indicator');
	const { hasNewContent, markAsSeen } = useWhatsNewIndicator(isWhatsNewIndicatorEnabled);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	const handleDropdownVisibleChange = useCallback((visible: boolean) => {
		setIsDropdownOpen(visible);
	}, []);

	// data-id attributes for analytics
	const items = useMemo(() => {
		const menuItems: MenuItem[] = [
			{
				key: 'whats_new',
				label: (
					<a
						data-id="whats_new"
						className="help-menu-item campaign"
						href="https://help.bringg.com/docs/whats-new"
						target="_blank"
						rel="noreferrer"
						onClick={markAsSeen}
					>
						<BringgIcon className="help-menu-item-icon campaign" iconName={BringgFontIcons.Campaign} />
						<Badge dot={hasNewContent} offset={BADGE_DOT_OFFSET}>
							{t('HELP_MENU.WHATS_NEW')}
						</Badge>
					</a>
				)
			},
			{
				key: 'help_center',
				label: (
					<a
						data-id="help_center"
						className="help-menu-item"
						href="https://help.bringg.com/"
						target="_blank"
						rel="noreferrer"
					>
						<BringgIcon className="help-menu-item-icon" iconName={BringgFontIcons.AutoStories} />
						{t('HELP_MENU.HELP_CENTER')}
					</a>
				)
			}
		];

		if (configuration.enable_support_ticket_on_web) {
			menuItems.push({
				key: 'submit_ticket',
				label: (
					<div data-id="submit_ticket" className="help-menu-item">
						<BringgIcon className="help-menu-item-icon" iconName={BringgFontIcons.Headset} />
						{t('HELP_MENU.SUBMIT_TICKET')}
					</div>
				),
				onClick: () => {
					executeAction(authStore.crossAppTransport, ModalAction.SUBMIT_TICKET_MODAL);
				}
			});
		}

		return menuItems;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [t, authStore.crossAppTransport, configuration.enable_support_ticket_on_web, hasNewContent]);
	return (
		<DropdownMenu
			// destroyOnHide = true for resize on navigation sidebar collapse
			destroyOnHide
			overlayClassName="help-menu-overlay"
			placement="right"
			noArrow={false}
			trigger={['click']}
			items={items}
			onDropdownVisibleChange={handleDropdownVisibleChange}
		>
			<div className="help-menu-container" data-test-id="help-menu-button">
				<span className="help-menu-icon-wrapper">
					<BringgIcon className="help-menu-title-icon" iconName={BringgFontIcons.Help} />
					{hasNewContent && !isDropdownOpen && <span className="help-menu-new-dot" />}
				</span>
				<span className="help-menu-title">{t('HELP_MENU.TITLE')}</span>
				{hasNewContent && !isDropdownOpen && (
					<img className="help-menu-new-badge" src={newBadgeSvg} alt="new" />
				)}
			</div>
		</DropdownMenu>
	);
});

export function HelpMenuPortal() {
	return <Portal element={<HelpMenu />} nodeId="help-menu-portal" />;
}
